import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { logger } from '../utils/console';

/**
 * Guard genérico baseado em roles para controle de acesso
 * Usa o estado já carregado no AuthService - sem nova chamada API
 * 
 * Uso nas rotas:
 * { path: 'medico', canActivate: [RoleGuard], data: { roles: ['medico'] } }
 * { path: 'admin', canActivate: [RoleGuard], data: { roles: ['admin', 'super'] } }
 */
@Injectable({
    providedIn: 'root'
})
export class RoleGuard implements CanActivate {

  constructor(
    private authService: AuthService,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {

    const allowedRoles: string[] = route.data['roles'] || [];

    // Se não houver roles especificadas, bloqueia acesso
    if (allowedRoles.length === 0) {
      logger.error('RoleGuard: Nenhuma role especificada na rota', state.url);
      this.router.navigate(['/auth/login']);
      return this.authService.usuario_as_obs.pipe(
        take(1),
        map(() => false)
      );
    }

    // Usa estado já carregado no AuthService - sem nova chamada API
    return this.authService.usuario_as_obs.pipe(
      take(1), // Evita memory leak
      map(user => {
        // Verifica se usuário está autenticado
        if (!user || !('email' in user)) {
          logger.warn('RoleGuard: Usuário não autenticado', state.url);
          this.router.navigate(['/auth/login'], {
            queryParams: { returnUrl: state.url }
          });
          return false;
        }

        // Verifica se usuário tem uma das roles permitidas
        const hasRole = allowedRoles.includes(user.tipo_usuario);
        
        if (!hasRole) {
          logger.warn(
            `RoleGuard: Acesso negado. Usuário: ${user.tipo_usuario}, Requerido: ${allowedRoles.join(' ou ')}`
          );
          this.router.navigate(['/auth/login']);
          return false;
        }

        return true;
      })
    );
  }
}
